import React, { useState, useEffect, useCallback } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import StandardButton from "../components/StandardButton";

const MODE_COMPONENT = "component";
const MODE_FULL_URL = "full";

const encodeWith = (text, mode) =>
  mode === MODE_FULL_URL ? encodeURI(text) : encodeURIComponent(text);

const URLEncoder = () => {
  const [input, setInput] = useState("https://example.com/search?query=hello world&sort=ascending&lang=en");
  const [encoded, setEncoded] = useState("");
  const [mode, setMode] = useState(MODE_COMPONENT);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  useEffect(() => {
    const id = searchParams.get("share");
    if (id) {
      api.get(`/url-encode/share/${id}`)
        .then(res => {
          const data = res.data;
          if (data.input && data.result) {
            setInput(data.input);
            handleLocalEncode(data.input, mode);
          }
        })
        .catch(() => {
          toast.error(t('server_error'));
        });
    }
  }, []);

  const handleLocalEncode = (text, useMode) => {
    try {
      const encodedText = encodeWith(text, useMode);
      setEncoded(encodedText);
      return encodedText;
    } catch (error) {
      toast.error(t('encoding_error'));
      return "";
    }
  };

  const handleEncode = useCallback(() => {
    if (!input.trim()) {
      toast.error(t('enter_text_to_encode'));
      return;
    }
    handleLocalEncode(input, mode);
  }, [input, mode, t]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return;
      if (e.altKey || e.shiftKey) return;
      e.preventDefault();
      handleEncode();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleEncode]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInput(newValue);
  };

  const handleCopy = async () => {
    if (!encoded) {
      toast.error(t('nothing_to_copy'));
      return;
    }
    try {
      await navigator.clipboard.writeText(encoded);
      toast.success(t('copy_success'));
    } catch {
      toast.error(t('failed_to_copy'));
    }
  };

  const handleShare = async () => {
    if (!input || !encoded) {
      toast.error(t('nothing_to_share'));
      return;
    }
    try {
      const response = await api.post(`/url-encode/share`, { input, result: encoded });
      const data = response.data;
      if (data.id) {
        const url = `${window.location.origin}/tools/url-encoder?share=${data.id}`;
        await navigator.clipboard.writeText(url);
        toast.success(t('share_success'));
      } else {
        toast.error(t('share_failed'));
      }
    } catch {
      toast.error(t('share_failed'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t('url_encoder_title')}
              </h1>
              <p className="text-gray-600 mt-1">{t('url_encoder_intro')}</p>
            </div>

            <textarea
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono text-sm"
              placeholder={t('enter_text_to_encode_placeholder')}
              value={input}
              onChange={handleInputChange}
            />

            <fieldset className="flex flex-col gap-2 text-sm text-gray-700">
              <legend className="font-medium text-gray-800 mb-1">
                {t('url_encoder_mode_label')}
              </legend>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="url-encoder-mode"
                  value={MODE_COMPONENT}
                  checked={mode === MODE_COMPONENT}
                  onChange={() => setMode(MODE_COMPONENT)}
                  className="h-4 w-4 mt-0.5"
                />
                <span>
                  <span className="font-medium">{t('url_encoder_mode_component')}</span>
                  <span className="block text-xs text-gray-500">
                    {t('url_encoder_mode_component_help')}
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="url-encoder-mode"
                  value={MODE_FULL_URL}
                  checked={mode === MODE_FULL_URL}
                  onChange={() => setMode(MODE_FULL_URL)}
                  className="h-4 w-4 mt-0.5"
                />
                <span>
                  <span className="font-medium">{t('url_encoder_mode_full')}</span>
                  <span className="block text-xs text-gray-500">
                    {t('url_encoder_mode_full_help')}
                  </span>
                </span>
              </label>
            </fieldset>

            <StandardButton
              onClick={handleEncode}
              type="secondary"
              className="w-full"
            >
              {t('encode_button')}
            </StandardButton>

            {encoded && (
              <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                <strong className="text-indigo-700">{t('encoded_output')}:</strong>
                <p className="break-all mt-2 text-gray-800 font-mono text-sm whitespace-pre-wrap">{encoded}</p>
                <div className="mt-4 flex gap-3">
                  <StandardButton
                    onClick={handleCopy}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t('copy_button')}
                  </StandardButton>

                  <StandardButton
                    onClick={handleShare}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t('share_button')}
                  </StandardButton>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default URLEncoder;
